/* =============================================================================
   beUNIQ — arch slider (homepage hero, sits above the split slideshow)

   Deliberately small and dependency free: the homepage already loads a lot,
   and this is the first thing a visitor sees.

   Two rules drive most of the decisions here.

   1. It must never take the scroll away from the reader. There is no wheel
      listener and no touchmove preventDefault anywhere in this file. Vertical
      panning is handed to the browser through `touch-action: pan-y` in CSS, so
      a swipe down the page always scrolls the page, even if the finger started
      inside the slider. Only a clearly horizontal drag is read as a swipe.

   2. It should not animate while nobody is looking. Autoplay stops when the
      section scrolls out of view, when the tab is hidden, on hover, and on
      keyboard focus, and never starts at all if the visitor has asked for
      reduced motion.
   ========================================================================== */

class BeuniqArchSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-arch-slide]'))
    if (this.slides.length === 0) return

    this.dots = Array.from(this.querySelectorAll('[data-arch-dot]'))
    this.live = this.querySelector('[data-arch-live]')
    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'))
    if (this.index < 0) this.index = 0

    this.interval = parseInt(this.dataset.autoplay, 10) || 3000
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.paused = false
    this.visible = true

    this.bind()
    this.go(this.index, true)
    this.start()
  }

  bind() {
    this.onPrev = () => this.step(-1, true)
    this.onNext = () => this.step(1, true)
    this.querySelectorAll('[data-arch-prev]').forEach((b) => b.addEventListener('click', this.onPrev))
    this.querySelectorAll('[data-arch-next]').forEach((b) => b.addEventListener('click', this.onNext))

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.go(i)
        this.restart()
      })
    })

    // Hover and focus pause. Touch devices do not get a hover state, which is
    // why the IntersectionObserver below matters more than this does.
    this.onEnter = () => { this.paused = true }
    this.onLeave = () => { this.paused = false }
    this.addEventListener('mouseenter', this.onEnter)
    this.addEventListener('mouseleave', this.onLeave)
    this.addEventListener('focusin', this.onEnter)
    this.addEventListener('focusout', this.onLeave)

    this.onKey = (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        this.step(-1, true)
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        this.step(1, true)
      } else {
        return
      }
      // Only swallow the key once we know it was a slider key, so Arrow keys
      // still scroll the page when the slider is not the thing in focus.
      event.preventDefault()
    }
    this.addEventListener('keydown', this.onKey)

    // Horizontal swipe only. Nothing here cancels the browser's own scrolling.
    let x0 = null
    let y0 = null
    this.onTouchStart = (event) => {
      const t = event.changedTouches[0]
      x0 = t.clientX
      y0 = t.clientY
    }
    this.onTouchEnd = (event) => {
      if (x0 === null) return
      const t = event.changedTouches[0]
      const dx = t.clientX - x0
      const dy = t.clientY - y0
      // Needs to be a decisive sideways move, not a slightly wobbly scroll.
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.step(dx < 0 ? 1 : -1, true)
      }
      x0 = null
      y0 = null
    }
    this.addEventListener('touchstart', this.onTouchStart, { passive: true })
    this.addEventListener('touchend', this.onTouchEnd, { passive: true })

    this.onVisibility = () => {
      if (document.hidden) {
        this.stop()
      } else {
        this.start()
      }
    }
    document.addEventListener('visibilitychange', this.onVisibility)

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting
          if (this.visible) {
            this.start()
          } else {
            this.stop()
          }
        },
        { threshold: 0.2 }
      )
      this.io.observe(this)
    }
  }

  go(next, initial) {
    const total = this.slides.length
    const previous = this.index
    this.index = (next + total) % total

    // Hold the outgoing slide underneath while the incoming one wipes across,
    // otherwise the wipe reveals the container background instead of the
    // slide it is replacing.
    if (!initial && previous !== this.index) {
      const leaving = this.slides[previous]
      if (leaving) {
        leaving.classList.add('is-leaving')
        clearTimeout(this.leaveTimer)
        this.leaveTimer = setTimeout(() => {
          this.slides.forEach((s) => s.classList.remove('is-leaving'))
        }, 900)
      }
    }

    this.slides.forEach((slide, i) => {
      const active = i === this.index
      slide.classList.toggle('is-active', active)
      if (active) slide.classList.remove('is-leaving')
      slide.setAttribute('aria-hidden', active ? 'false' : 'true')
      // Keep inactive slides out of the tab order so the keyboard does not
      // wander into buttons nobody can see.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) {
          el.removeAttribute('tabindex')
        } else {
          el.setAttribute('tabindex', '-1')
        }
      })
    })

    this.dots.forEach((dot, i) => {
      const active = i === this.index
      dot.classList.toggle('is-active', active)
      dot.setAttribute('aria-selected', active ? 'true' : 'false')
    })

    if (this.live && !initial) {
      this.live.textContent = `Slide ${this.index + 1} of ${total}`
    }
  }

  step(by, manual) {
    this.go(this.index + by)
    if (manual) this.restart()
  }

  start() {
    if (this.reduced || this.slides.length < 2) return
    if (this.timer || document.hidden || !this.visible) return
    this.timer = setInterval(() => {
      if (!this.paused) this.step(1)
    }, this.interval)
  }

  stop() {
    clearInterval(this.timer)
    this.timer = null
  }

  clearLeaving() {
    clearTimeout(this.leaveTimer)
    this.slides.forEach((s) => s.classList.remove('is-leaving'))
  }

  restart() {
    this.stop()
    this.start()
  }

  disconnectedCallback() {
    this.stop()
    this.clearLeaving()
    document.removeEventListener('visibilitychange', this.onVisibility)
    if (this.io) this.io.disconnect()
  }
}

if (!customElements.get('beuniq-arch-slider')) {
  customElements.define('beuniq-arch-slider', BeuniqArchSlider)
}
